import { Handle, NodeProps, NodeResizer, Position } from '@xyflow/react'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import { resolveHighlightColor } from 'features/processFlowDiagrams/utils/helpers'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { PfdLabelPosition, PfdNode, PfdShapeStyle } from 'types'

const MIN_WIDTH = 40
const MIN_HEIGHT = 30

const getStrokeDasharray = (style?: PfdShapeStyle): string | undefined => {
  switch (style) {
    case 'dashed':
      return '8 4'
    case 'dotted':
      return '2 2'
    default:
      return undefined
  }
}

const getFill = (style?: PfdShapeStyle, color?: string): string => {
  if (style === 'filled') return color || 'currentColor'
  return 'none'
}

const getFillOpacity = (style?: PfdShapeStyle): number =>
  style === 'filled' ? 0.2 : 1

const NodeWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const Container = styled.div<{
  $shapeColor?: string
  $isSelected: boolean
  $highlightColor?: string
}>`
  width: 100%;
  height: 100%;
  position: relative;
  color: ${({ $shapeColor, theme }) =>
    $shapeColor || theme.forecast.activeText};

  ${({ $isSelected, $highlightColor }) =>
    $isSelected &&
    $highlightColor &&
    `
    filter: drop-shadow(0 0 6px ${$highlightColor}) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
  `}
`

const RectSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
`

const getLabelPositionStyles = (position: PfdLabelPosition) => {
  switch (position) {
    case 'top':
      return `
        top: -1.25rem;
        left: 50%;
        transform: translateX(-50%);
      `
    case 'left':
      return `
        top: 50%;
        left: -0.5rem;
        transform: translate(-100%, -50%);
      `
    case 'right':
      return `
        top: 50%;
        right: -0.5rem;
        transform: translate(100%, -50%);
        left: auto;
      `
    case 'center':
      return `
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      `
    case 'bottom':
    default:
      return `
        bottom: -1.25rem;
        left: 50%;
        transform: translateX(-50%);
      `
  }
}

const NodeLabel = styled.div<{
  $position?: PfdLabelPosition
  $backgroundColor?: string
  $textColor?: string
}>`
  position: absolute;
  ${({ $position }) => getLabelPositionStyles($position ?? 'bottom')}
  background: ${({ $backgroundColor, theme }) =>
    $backgroundColor || theme.forecast.background2};
  color: ${({ $textColor, theme }) => $textColor || theme.forecast.activeText};
  border-radius: 4px;
  padding: 0 0.25rem;
  font-size: 0.75rem;
  line-height: 1rem;
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
`

/** Resizable rectangle node with draggable edges. */
export const RectangleNode: React.FC<NodeProps<PfdNode>> = ({ id, data }) => {
  const { selectedNode, editingEnabled } = useContext(
    ProcessFlowDiagramBuilderContext
  )

  const isSelected = useMemo(() => selectedNode?.id === id, [selectedNode, id])

  const selectedHighlight = useMemo(() => {
    if (!selectedNode) return undefined
    const status = selectedNode.data.status
    return resolveHighlightColor(String(status)) || selectedNode.data.shapeColor
  }, [selectedNode])

  return (
    <NodeWrapper>
      <NodeResizer
        isVisible={isSelected && editingEnabled}
        minWidth={MIN_WIDTH}
        minHeight={MIN_HEIGHT}
        lineStyle={{ borderColor: data.shapeColor || 'currentColor' }}
        handleStyle={{ width: 8, height: 8 }}
      />

      <Container
        $shapeColor={data.shapeColor}
        $isSelected={isSelected}
        $highlightColor={selectedHighlight}>
        <Handle type='source' position={Position.Top} id='top-source' />
        <Handle type='source' position={Position.Bottom} id='bottom-source' />
        <Handle type='source' position={Position.Left} id='left-source' />
        <Handle type='source' position={Position.Right} id='right-source' />

        <RectSvg viewBox='0 0 100 100' preserveAspectRatio='none'>
          <rect
            x='2'
            y='2'
            width='96'
            height='96'
            rx='3'
            stroke='currentColor'
            strokeWidth='4'
            vectorEffect='non-scaling-stroke'
            fill={getFill(data.shapeStyle, data.shapeColor)}
            fillOpacity={getFillOpacity(data.shapeStyle)}
            strokeDasharray={getStrokeDasharray(data.shapeStyle)}
          />
        </RectSvg>
      </Container>

      {data.label && (
        <NodeLabel
          title={data.label}
          $position={data.labelPosition}
          $backgroundColor={data.shapeColor}
          $textColor={data.textColor}>
          {data.label}
        </NodeLabel>
      )}
    </NodeWrapper>
  )
}
